import { DateTime } from '@ksyos/date-time';
import { Radio } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { Process } from '@common/interfaces/processes';
import type { WatchPatSleepStudyPdfData } from '@common/validation/customControls/osas/osasCloudPatExaminationSchema';
import type { OsasConsultExaminationData } from '@common/validation/customControls/osas/osasConsultSchemas/types';
import type { ValidationErrors } from '@frontend/utils';
import { CustomControlCode, ServiceCode, YesOrNoOrNotApplicable } from '@common/enums';
import { findSingleProcessByServiceCode, getLastProcessActivityByCustomControlCodes } from '@common/utils/processUtil';
import { booleanAsJaNee } from '@common/utils/textUtils';
import {
    DateInputFormGroup,
    RadioGroupFormGroup,
    TextAreaFormGroup,
    TextInputFormGroup,
} from '@frontend/components/pages/episodeCategory/customControls/processActivityFormGroupFields';
import { usePatientEpisodeCategoryContext } from '@frontend/utils/hooks/usePatientEpisodeCategoryContext';
import { OsasConsultOtherRelevantFindingsFormGroup } from './SleepStudyFindingsOtherFindingsFormGroup';

export type SleepStudyFindingsData = OsasConsultExaminationData &
    Pick<
        WatchPatSleepStudyPdfData,
        'pAhi3Percent' | 'odi3Percent' | 'odi4Percent' | 'oxygenSaturationMeanValue' | 'oxygenSaturationMinValue'
    >;

export type SleepStudyFindingsFormGroupProps = {
    data: SleepStudyFindingsData;
    errors: ValidationErrors;
    onChange: (data: Partial<SleepStudyFindingsData>, validateKeys?: string | string[] | undefined) => void;
    processActivityId?: string;
};

export function SleepStudyFindings(props: Readonly<SleepStudyFindingsFormGroupProps>) {
    const { data, errors, onChange, processActivityId } = props;
    const [examinationCustomControlData, setExaminationCustomControlData] = useState<WatchPatSleepStudyPdfData | null>(
        null
    );
    const { processes } = usePatientEpisodeCategoryContext();
    const process = findSingleProcessByServiceCode(processes, ServiceCode.OSAS);

    useEffect(() => {
        if (!process || processActivityId) {
            return;
        }

        const examinationActivity = getLatestExamination(process);

        if (examinationActivity) {
            setExaminationCustomControlData(examinationActivity.customControlData);
            if (examinationActivity.created_at) {
                onChange({
                    examinationDate: examinationActivity.created_at,
                });
            }
        }
    }, [processes]);

    useEffect(() => {
        if (examinationCustomControlData) {
            const { T90Percent, centralSaGreaterThan10 } = examinationCalculations(examinationCustomControlData);

            onChange({
                T90Percent: T90Percent,
                centralSaGreaterThan10: centralSaGreaterThan10,
                odi3Percent: examinationCustomControlData.odi3Percent?.toString(),
                odi4Percent: examinationCustomControlData.odi4Percent?.toString(),
                oxygenSaturationMeanValue: examinationCustomControlData.oxygenSaturationMeanValue?.toString(),
                oxygenSaturationMinValue: examinationCustomControlData.oxygenSaturationMinValue?.toString(),
                pAhi3Percent: examinationCustomControlData.pAhi3Percent?.toString(),
            });
        }
    }, [examinationCustomControlData]);

    if (!process) {
        return <></>;
    }

    return (
        <>
            <DateInputFormGroup
                fieldKey="examinationDate"
                label="Datum slaaponderzoek"
                maxDate={data.examinationDate ? DateTime.from(data.examinationDate).format() : undefined}
                value={data.examinationDate}
            />
            <TextInputFormGroup
                fieldKey="pAhi3Percent"
                label="Gemiddelde AHI"
                placeholder=""
            />
            <TextInputFormGroup
                fieldKey="odi3Percent"
                label="Gemiddelde ODI 3%"
                placeholder=""
            />
            <TextInputFormGroup
                fieldKey="odi4Percent"
                label="Gemiddelde ODI 4%"
                placeholder=""
            />
            <TextInputFormGroup
                fieldKey="oxygenSaturationMeanValue"
                label="Gemiddelde saturatie %"
                placeholder=""
            />
            <TextInputFormGroup
                fieldKey="oxygenSaturationMinValue"
                label="Laagst gemeten saturatie %"
                placeholder=""
            />
            <TextInputFormGroup
                fieldKey="T90Percent"
                label="T90 %"
                placeholder=""
            />

            <RadioGroupFormGroup
                fieldKey="centralSaGreaterThan10"
                label="Centrale SA >10"
                onChange={async (value) => {
                    onChange({ centralSaGreaterThan10: Boolean(value === 'Ja') }, 'centralSaGreaterThan10');
                }}
                radioGroupLayout={'row'}
                value={booleanAsJaNee(data?.centralSaGreaterThan10) ?? undefined}
            >
                <Radio
                    key={booleanAsJaNee(true)?.toString()}
                    label="Ja"
                    value={booleanAsJaNee(true)?.toString()}
                />
                <Radio
                    key={booleanAsJaNee(false)?.toString()}
                    label="Nee"
                    value={booleanAsJaNee(false)?.toString()}
                />
            </RadioGroupFormGroup>
            <RadioGroupFormGroup
                fieldKey="relevantHypoxemia"
                label="Relevante hypoxemie"
                onChange={async (value) => {
                    onChange({ relevantHypoxemia: Boolean(value === 'Ja') }, 'relevantHypoxemia');
                }}
                radioGroupLayout={'row'}
                value={booleanAsJaNee(data?.relevantHypoxemia) ?? undefined}
            >
                <Radio
                    key={booleanAsJaNee(true)?.toString()}
                    label="Ja"
                    value={booleanAsJaNee(true)?.toString()}
                />
                <Radio
                    key={booleanAsJaNee(false)?.toString()}
                    label="Nee"
                    value={booleanAsJaNee(false)?.toString()}
                />
            </RadioGroupFormGroup>

            {data.relevantHypoxemia && (
                <TextAreaFormGroup
                    fieldKey={'remarkRelevantHypoxemia'}
                    label={'Toelichting'}
                />
            )}

            <RadioGroupFormGroup
                fieldKey="positionDependent"
                label="Positieafhankelijk"
                onChange={async (value) => {
                    onChange({ positionDependent: value as YesOrNoOrNotApplicable }, 'positionDependent');
                }}
                radioGroupLayout={'row'}
                value={data?.positionDependent ?? undefined}
            >
                <Radio
                    key={YesOrNoOrNotApplicable.yes}
                    label="Ja"
                    value={YesOrNoOrNotApplicable.yes}
                />
                <Radio
                    key={YesOrNoOrNotApplicable.no}
                    label="Nee"
                    value={YesOrNoOrNotApplicable.no}
                />
                <Radio
                    key={YesOrNoOrNotApplicable.notApplicable}
                    label="N.v.t."
                    value={YesOrNoOrNotApplicable.notApplicable}
                />
            </RadioGroupFormGroup>

            {data.positionDependent === YesOrNoOrNotApplicable.yes && (
                <TextAreaFormGroup
                    fieldKey={'remarkPositionDependent'}
                    label={'Toelichting'}
                />
            )}
            <OsasConsultOtherRelevantFindingsFormGroup
                data={data}
                errors={errors}
                onChange={onChange}
            />
        </>
    );
}

function examinationCalculations(examinationActivity: WatchPatSleepStudyPdfData) {
    const satBelow90InSeconds = Number(examinationActivity.satBelow90InSeconds);
    const totalSleepTimeInSeconds = Number(examinationActivity.totalSleepTimeInSeconds);

    const canCalculateT90Percent =
        !isNaN(satBelow90InSeconds) && !isNaN(totalSleepTimeInSeconds) && totalSleepTimeInSeconds > 0;

    const T90Percent = canCalculateT90Percent
        ? ((satBelow90InSeconds / totalSleepTimeInSeconds) * 100).toFixed(2)
        : null;

    const centralSaGreaterThan10 =
        examinationActivity.AHICentral !== null && examinationActivity.AHICentral !== undefined
            ? Number(examinationActivity.AHICentral) > 10
            : false;

    return {
        T90Percent,
        centralSaGreaterThan10,
    };
}

function getLatestExamination(process: Process) {
    const latestConsult = getLastProcessActivityByCustomControlCodes(process, [
        CustomControlCode.OSASConsult,
        CustomControlCode.OSASFollowUpConsult,
    ]);

    const latestExamination = getLastProcessActivityByCustomControlCodes(process, [
        CustomControlCode.OSASCloudPatFollowUpExamination,
        CustomControlCode.OSASCloudPatExamination,
    ]);

    if (!latestConsult || latestExamination?.created_at > latestConsult?.created_at) {
        return latestExamination;
    }

    return undefined;
}
